import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'

import { v4 as uuidv4 } from 'uuid'

import { SidePanelModal } from '~/components/SidePanelModal'
import { SIDE_PANEL_PORTAL_ID } from '~/constants'

export interface SidePanelContextData {
  addSidePanel(message: Omit<SidePanelMessage, 'id'> & { id?: string }): void
  removeSidePanel({
    callback,
    id
  }?: {
    callback?: () => void
    id?: string
  }): void
}

export const SidePanelContext = createContext<SidePanelContextData | null>(null)

const SidePanelProvider = ({ children }: PropsWithChildren) => {
  const [messages, setMessages] = useState<SidePanelMessage[]>([])

  useEffect(() => {
    if (messages.length > 0) {
      document.body.classList.add('sidePanel__open')
      document.body.style.overflow = 'hidden'
      return
    }

    document.body.classList.remove('sidePanel__open')
    document.body.style.overflow = ''
  }, [messages])

  useEffect(
    () => () => {
      document.body.classList.remove('sidePanel__open')
      document.body.style.overflow = ''
    },
    []
  )

  // id is optional
  const addSidePanel = useCallback(
    ({
      id,
      title,
      content,
      type,
      size = 'medium',
      callbackRemove
    }: Omit<SidePanelMessage, 'id'> & { id?: string }) => {
      const sidePanel: SidePanelMessage = {
        id: id || uuidv4(),
        title,
        content,
        type,
        size,
        callbackRemove
      }

      setMessages(state => [...state, sidePanel])
    },
    []
  )

  const removeSidePanel = useCallback(
    ({ callback, id }: { callback?: () => void; id?: string } = {}) => {
      callback?.()

      setMessages(prevState =>
        id ? prevState.filter(item => item.id !== id) : []
      )
    },
    []
  )

  return (
    <SidePanelContext.Provider value={{ addSidePanel, removeSidePanel }}>
      {children}
      <div id={SIDE_PANEL_PORTAL_ID} />
      {messages.map(message => (
        <SidePanelModal
          key={message.id}
          message={message}
          onClose={callback => removeSidePanel({ callback, id: message.id })}
        />
      ))}
    </SidePanelContext.Provider>
  )
}

function useSidePanel(): SidePanelContextData {
  const context = useContext(SidePanelContext)

  if (!context) {
    throw new Error('useSidePanel must be used within an SidePanelProvider')
  }

  return context
}

export { SidePanelProvider, useSidePanel }
